import { mapValues, pick } from "lodash";

import APIClient from "web_modules/APIClient";

import { get60DayLockParRateFromRate } from "@eave/shared/utils/rates";

import { decimalToThreePlaces } from "web_modules/capital_markets_dashboard/shared/utils";
import { ILoanProgram, ILoanType, IRate, IRateAdjusters } from "../interfaces";

export const getLoanProgramsForLoanType = async (id: number): Promise<ILoanProgram[]> => {
  return APIClient.get(`/cm/loan_types/${id}/loan_programs`).then(({ loan_programs }) => {
    return loan_programs.map(p => pick(p, "id", "loan_type_id", "name"));
  });
};

export const getRateForLoanProgram = async (id: number, name: string): Promise<IRate> => {
  return APIClient.get(`/cm/loan_programs/${id}/rates`).then(({ rates }) => {
    const r = rates[0];

    return {
      id: r.id,
      loan_program_id: r.loan_program_id,
      loan_program_name: name,
      rate: get60DayLockParRateFromRate(r),
      created_at: r.created_at,
    };
  });
};

export const createRateForLoanProgram = async (
  id: number,
  name: string,
  rate: string,
): Promise<IRate> => {
  return APIClient.post(`/cm/loan_programs/${id}/rates`, {
    lock_periods: {
      "60": [
        {
          rate,
          price: "0",
          par: true,
        },
      ],
    },
  }).then(({ lock_periods }) => {
    return {
      id: lock_periods.id,
      loan_program_id: lock_periods.loan_program_id,
      loan_program_name: name,
      rate: get60DayLockParRateFromRate(lock_periods),
      created_at: lock_periods.created_at,
    };
  });
};

export const refreshRates = async (loan_type: string): Promise<string> => {
  return APIClient.post(`/cm/base_rates/${loan_type}`, {}).then(({ message }) => message);
};

export const updateLoanType = async (
  id: number,
  target_price: string,
  target_purchase_price: string,
  target_loan_amount: string,
  target_fico: number,
): Promise<ILoanType> => {
  return APIClient.put<{ loan_type: ILoanType }>(`/cm/loan_types/${id}`, {
    target_price,
    target_purchase_price,
    target_loan_amount,
    target_fico,
  }).then(({ loan_type }) => loan_type);
};

export const getAdjustersForLoanType = async (loanTypeId: number): Promise<IRateAdjusters> =>
  APIClient.get<{ rate_adjusters: IRateAdjusters }>(
    `/cm/loan_types/${loanTypeId}/rate_adjusters`,
  ).then(({ rate_adjusters }) => {
    const {
      id,
      loan_type_id,
      adjusters,
      fico_x_ltv: { fico, ltv, matrix },
      created_at,
      ltv: ltvBands,
      ltv_adjusters,
    } = rate_adjusters;

    return {
      id,
      loan_type_id,
      ltv: ltvBands,
      ltv_adjusters,
      adjusters: mapValues(adjusters, a => decimalToThreePlaces(a)),
      fico_x_ltv: {
        fico,
        ltv,
        matrix: matrix.map(row => row.map(val => decimalToThreePlaces(val))),
      },
      created_at,
    } as IRateAdjusters;
  });

export const updateAdjustersForLoanType = async (
  loanTypeId: number,
  updated: IRateAdjusters,
): Promise<IRateAdjusters> => {
  return APIClient.post<{ rate_adjusters: IRateAdjusters }>(
    `/cm/loan_types/${loanTypeId}/rate_adjusters`,
    {
      rate_adjusters: updated.adjusters,
      fico_x_ltv: {
        fico: updated.fico_x_ltv.fico,
        ltv: updated.fico_x_ltv.ltv,
        matrix: updated.fico_x_ltv.matrix.map(row => row.map(val => decimalToThreePlaces(val))),
      },
      ltv: updated.ltv,
      ltv_adjusters: updated.ltv_adjusters,
    },
  ).then(({ rate_adjusters }) => {
    const {
      id,
      loan_type_id,
      adjusters,
      fico_x_ltv: { fico, ltv, matrix },
      created_at,
      ltv: ltvValues,
      ltv_adjusters,
    } = rate_adjusters;

    return {
      id,
      loan_type_id,
      adjusters: mapValues(adjusters, a => decimalToThreePlaces(a)),
      fico_x_ltv: {
        fico,
        ltv,
        matrix: matrix.map(row => row.map(val => decimalToThreePlaces(val))),
      },
      created_at,
      ltv: ltvValues,
      ltv_adjusters,
    } as IRateAdjusters;
  });
};
